import {
	DropdownDeleteField,
	ErrorText,
	RedText,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/components/documentSectionStyledComponents';
import {
	type DocumentWorkflowFileTct,
	PendingSignaturePlacementDto,
} from '@dealstack/shared/lib/generated';
import {
	ChipChildrenWrapper,
	CounterpartiesChipsWrapper,
	CounterpartyInputWrapper,
	GuidanceNoteDocument,
	GuidanceNoteWrapper,
	InternalGroupWrapper,
	LeftPanel,
	NumberInputWrapper,
	RadioGroupWrapper,
	RightPanel,
	ScrollableSection,
	SignatureTypeRow,
	SignatureTypeSwitchWrapper,
	StyledDivider,
	StyledGridColumn,
	StyledIconButton,
	StyledNumberInput,
	StyledNumberInputGroup,
	StyledTextInput,
	TemplateTypesHorizontalList,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/components/templateSectionStyledComponents';
import { TemplateTypeCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/components/TemplateTypeCard';
import { TSCheckbox } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/components/TSCheckbox';
import {
	COUNTERPARTY_FORM,
	INPUT_MAX_AMOUNT,
	INPUT_MIN_AMOUNT,
	INPUT_STEP,
	useCounterpartiesFormController,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/components/useCounterpartiesFormController';
import { useTemplateCardTypes } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/components/useTemplateCardTypes';
import {
	Counterparty,
	DOCUMENT_SETTINGS_MODAL_FIELDS,
	DocumentSettings,
	DocumentSettingsFieldNames,
	TemplateTypesOptions,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/types';
import { DocumentCard } from '@dealstack/frontend/shared/components/DocumentCard/DocumentCard';
import {
	CHIP_THEME,
	COLOR,
	Chips as ChipsV2,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date';
import {
	DataFileTct,
	DocumentSignatureType,
	DocumentTemplateType,
} from '@dealstack/shared/lib/generated';
import { ActionIcon, Grid, Modal, Radio, Text } from '@mantine/core';
import { clsx } from 'clsx';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { isEqual, omit } from 'lodash';
import { TemplatePredefinedOptions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/templatePredefinedOptions';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useUploadFile } from '@dealstack/frontend/shared/components/DragAndDrop/useUploadFile';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { uploadFile } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { PendingPlacementEditor } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/placement/PendingPlacementEditor';
import { canPlaceSdwSignatures } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/placement/canPlaceSdwSignatures';
import { hasOverlappingPendingPlacements } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/placement/hasOverlappingPendingPlacements';
import { enumerateClientSlots } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/placement/clientSideSlots';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

const DEFAULT_TEXT_SIZE = 14;
const SEMI_BOLD_FONT_WEIGHT = 600;
const NORMAL_FONT_WEIGHT = 14;
const SMALL_TEXT_SIZE = 9;
const SIGNATORY_STEPPER_ICON_SIZE = 22;
const ONE_COLUMN_PER_ROW = 12;
const TWO_COLUMNS_PER_ROW = 6;

interface Props {
	className?: string;
	isUploadingGuidanceNote: boolean;
	selectedDocumentWorkflowId: string;
	documentsDataArray: DocumentSettings[];
	documentFiles: DocumentWorkflowFileTct[];
	onDataChange: (
		selectedDocumentWorkflowFileId: string,
		changedField: DocumentSettingsFieldNames,
		value: string | boolean | DataFileTct | undefined
	) => void;
	setTemplateTypeToChange: (templateType: TemplateTypesOptions['name']) => void;
	setIsUploadingGuidanceNote: (isUploadingGuidanceNote: boolean) => void;
	changeDocumentTemplateType: (
		selectedDocumentWorkflowFileId: string,
		templateType: TemplateTypesOptions['name']
	) => void;
	setDocumentsDataArray: Dispatch<SetStateAction<DocumentSettings[]>>;
	setCounterpartyToDelete: Dispatch<SetStateAction<{ chipId: string; name: string }>>;
}

const TemplateSection = ({
	isUploadingGuidanceNote,
	documentsDataArray,
	selectedDocumentWorkflowId,
	documentFiles,
	setTemplateTypeToChange,
	onDataChange,
	setIsUploadingGuidanceNote,
	changeDocumentTemplateType,
	setDocumentsDataArray,
	setCounterpartyToDelete,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const DEFAULT_DATA = { ...TemplatePredefinedOptions.CUSTOM, templateType: undefined };

	const currentDocumentData =
		documentsDataArray.find(
			(document) => document.documentWorkflowFileId === selectedDocumentWorkflowId
		) ?? DEFAULT_DATA;

	const onTemplateSelected = (
		selectedDocumentWorkflowFileId: string,
		templateType: TemplateTypesOptions['name']
	) => {
		if (currentDocumentData.templateType === DocumentTemplateType.CUSTOM) {
			const omittedFields: DocumentSettingsFieldNames[] = [
				'documentWorkflowFileId',
				'templateType',
				'counterparties',
				'guidanceNote',
			];
			const result = isEqual(
				omit(currentDocumentData, omittedFields),
				omit(TemplatePredefinedOptions[DocumentTemplateType.CUSTOM], omittedFields)
			);
			if (!result) {
				setTemplateTypeToChange(templateType);
				return;
			}
		}
		changeDocumentTemplateType(selectedDocumentWorkflowFileId, templateType);
	};

	const SDWTemplateTypes = useTemplateCardTypes();

	const onSignatureTypeSwitchClicked = (currentSignatureType: string) => {
		let updatedType: DocumentSignatureType = DocumentSignatureType.WET_INK;
		if (currentSignatureType === DocumentSignatureType.WET_INK) {
			updatedType = DocumentSignatureType.ELECTRONIC;
		}
		onDataChange(
			currentDocumentData.documentWorkflowFileId,
			DOCUMENT_SETTINGS_MODAL_FIELDS.SIGNATURE_TYPE,
			updatedType
		);
	};

	const addCounterparty = (counterparty: Counterparty) => {
		setDocumentsDataArray((documentTemplateData: DocumentSettings[]) => {
			return documentTemplateData.map((documentTemplate) => {
				if (
					documentTemplate.documentWorkflowFileId !==
					currentDocumentData.documentWorkflowFileId
				) {
					return documentTemplate;
				}
				return {
					...documentTemplate,
					counterparties: [...documentTemplate.counterparties, counterparty],
				};
			});
		});
	};

	const onOpenCounterpartyDeleteAlert = (chipId: string, counterpartyName: string) => {
		setCounterpartyToDelete({ chipId, name: counterpartyName });
	};

	const { counterpartyForm, onSubmit } = useCounterpartiesFormController(addCounterparty);

	const {
		documentWorkflowFileId,
		guidanceNote,
		templateType,
		witnessesRequired,
		signatureType,
		individualScheduleRequired,
		isSignatureAdvanced,
		isToBeSubmitted,
		counterparties,
		counterpartiesIncluded,
		useManualSignaturePlacement,
		showStampMetadata,
	} = currentDocumentData;

	const { dealId } = useRequiredParams('dealId');

	const updatePlacementsForCurrentDoc = (placements: PendingSignaturePlacementDto[]) => {
		setDocumentsDataArray((prev) =>
			prev.map((doc) =>
				doc.documentWorkflowFileId === documentWorkflowFileId
					? { ...doc, signaturePlacements: placements }
					: doc
			)
		);
	};

	const updateShowStampMetadataForCurrentDoc = (next: boolean) => {
		setDocumentsDataArray((prev) =>
			prev.map((doc) =>
				doc.documentWorkflowFileId === documentWorkflowFileId
					? { ...doc, showStampMetadata: next }
					: doc
			)
		);
	};

	const toggleManualPlacement = (next: boolean) => {
		setDocumentsDataArray((prev) =>
			prev.map((doc) =>
				doc.documentWorkflowFileId === documentWorkflowFileId
					? {
							...doc,
							useManualSignaturePlacement: next,
							signaturePlacements: next ? doc.signaturePlacements ?? [] : [],
						}
					: doc
			)
		);
	};

	const placementSlots = useMemo(
		() =>
			enumerateClientSlots({
				counterparties: counterpartiesIncluded
					? counterparties.map((cp) => ({ name: cp.name, amount: cp.amount }))
					: [],
			}),
		[counterpartiesIncluded, counterparties]
	);

	const currentDocumentFileForPlacement = documentFiles.find(
		(documentWorkflowFile) => documentWorkflowFile.id === selectedDocumentWorkflowId
	);

	const tPrefix = 'investors.simple_document_workflow.template_section';
	const tTemplateOptionsPrefix = `${tPrefix}.template_options`;

	const isRadioButtonSelected = !!templateType;

	const isSignatureTypeElectronic = signatureType === DocumentSignatureType.ELECTRONIC;

	const isSignatureTypeWetInk = signatureType === DocumentSignatureType.WET_INK;

	const isSignatureRequired = isSignatureTypeElectronic || isSignatureTypeWetInk;

	// When both "To be submitted" and "Signature required" are enabled,
	// wet-ink is not allowed — only electronic signing is supported
	const isSubmittableWithSignature = isToBeSubmitted && isSignatureRequired;

	const isCounterpartiesIncluded = counterpartiesIncluded && isSignatureRequired;

	const isGDPRNoticeTemplateTypeSelected = templateType === DocumentTemplateType.GDPR_NOTICE;

	const isPreFillableFormTemplateSelected =
		templateType === DocumentTemplateType.PRE_FILLABLE_FORM;

	const currentDocumentFile = documentFiles.find(
		(documentWorkflowFile) => documentWorkflowFile.id === selectedDocumentWorkflowId
	);

	const isGeneratableDocumentSelected = currentDocumentFile?.isGeneratableDocument;

	// Generatable/pre-fillable documents with signature must use electronic only — no counterparties, no witnessing
	const isAutoTemplateWithSignature =
		(isGeneratableDocumentSelected ||
			isGDPRNoticeTemplateTypeSelected ||
			isPreFillableFormTemplateSelected) &&
		isSignatureRequired;

	// Sub-options (signature type, witnessing, counterparties) are locked when either
	// submittable+signature or auto-template+signature
	const isSignatureSubOptionsLocked = isSubmittableWithSignature || isAutoTemplateWithSignature;

	// Wet ink has no witness signers, and documents sent from here always get individual
	// copies, so there is no generated signature page to print witness fields on either.
	const isWitnessingAvailable = isSignatureTypeElectronic && !isSignatureSubOptionsLocked;

	// Placement is a separate question from that lock: a signable form (a pre-fillable form or
	// a submittable upload, once electronic) has stable pagination and can be placed on.
	const canPlaceSignaturesManually = canPlaceSdwSignatures({
		signatureType,
		templateType,
		isGeneratableDocument: isGeneratableDocumentSelected,
	});

	const isTemplateCardSectionDisabled = !selectedDocumentWorkflowId;

	const disabledConfig =
		isTemplateCardSectionDisabled ||
		isUploadingGuidanceNote ||
		isGDPRNoticeTemplateTypeSelected ||
		isPreFillableFormTemplateSelected ||
		isGeneratableDocumentSelected;

	const disabledSignatureConfig = isTemplateCardSectionDisabled || isUploadingGuidanceNote;

	const disableTemplateTypeSelect =
		isPreFillableFormTemplateSelected ||
		isGDPRNoticeTemplateTypeSelected ||
		isGeneratableDocumentSelected;

	const [isGuidanceDropdownOpen, setIsGuidanceDropdownOpen] = useState(false);
	const [isPlacementModalOpen, setIsPlacementModalOpen] = useState(false);

	const signaturePlacements = currentDocumentData.signaturePlacements ?? [];
	const placedCount = signaturePlacements.length;
	const totalSlots = placementSlots.length;

	const uploadFileRequest = useWrappedRequest(uploadFile, undefined, {
		onSuccessDefault({ payload }) {
			const file = payload;
			if (!file || currentDocumentData.guidanceNote === file) {
				return;
			}
			onDataChange(
				currentDocumentData.documentWorkflowFileId,
				DOCUMENT_SETTINGS_MODAL_FIELDS.GUIDANCE_NOTE,
				file
			);
			setIsUploadingGuidanceNote(false);
		},
	});

	const { onUpload } = useUploadFile({
		uploadFileRequest,
	});

	const uploadOnDrop = async (files: File[]) => {
		files.forEach((file) => onUpload(file));
		setIsUploadingGuidanceNote(true);
	};
	const onFilesDrop = async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
		if (acceptedFiles?.length !== 1 || rejectedFiles?.length) {
			return;
		}
		await uploadOnDrop(acceptedFiles);
	};

	const { open, fileRejections, getInputProps } = useDropzone({
		onDrop: onFilesDrop,
		noClick: true,
		accept: { 'application/pdf': ['.pdf'] },
		maxFiles: 1,
	});

	const showInvalidTypeView = fileRejections?.some((rejection) =>
		rejection.errors.some((error) => error.code === 'file-invalid-type')
	);

	useEffect(() => {
		setIsGuidanceDropdownOpen(false);
	}, []);

	const removeGuidanceNote = (documentWorkflowFileId: string) => {
		onDataChange(
			documentWorkflowFileId,
			DOCUMENT_SETTINGS_MODAL_FIELDS.GUIDANCE_NOTE,
			undefined
		);
		setIsGuidanceDropdownOpen(false);
	};

	return (
		<Container>
			<Grid gutter="xs">
				<Grid.Col
					span={ONE_COLUMN_PER_ROW}
					className={clsx({
						disabled: isTemplateCardSectionDisabled || isUploadingGuidanceNote,
					})}
				>
					<TemplateTypesHorizontalList>
						<Text
							fz={DEFAULT_TEXT_SIZE}
							fw={SEMI_BOLD_FONT_WEIGHT}
							sx={{ marginRight: '1rem' }}
						>
							{tMipManage(`${tPrefix}.apply_template`)}
						</Text>
						<Radio.Group
							value={templateType}
							onChange={(value) => {
								if (disableTemplateTypeSelect) {
									return;
								}
								onTemplateSelected(selectedDocumentWorkflowId, value);
							}}
						>
							<RadioGroupWrapper>
								{Object.entries(SDWTemplateTypes).map(([key, item]) => {
									return (
										<TemplateTypeCard
											key={key}
											disabled={disableTemplateTypeSelect}
											iconVariant={item.icon}
											label={item.name}
											cardValue={key}
											noWrap
											isSelected={item.name === templateType}
											onCardClick={() => {
												onTemplateSelected(selectedDocumentWorkflowId, key);
											}}
										/>
									);
								})}
							</RadioGroupWrapper>
						</Radio.Group>
					</TemplateTypesHorizontalList>
				</Grid.Col>
				<StyledGridColumn
					span={TWO_COLUMNS_PER_ROW}
					className={clsx({
						disabled: disabledSignatureConfig,
					})}
				>
					<LeftPanel>
						<TSCheckbox
							bold
							checked={isSignatureRequired}
							onChange={() => {
								let newSignatureType: DocumentSignatureType;
								if (isSignatureRequired) {
									// Unchecking signature required
									newSignatureType = isToBeSubmitted
										? DocumentSignatureType.SUBMITTABLE
										: DocumentSignatureType.REVIEW;
								} else {
									// Checking signature required — always ELECTRONIC
									newSignatureType = DocumentSignatureType.ELECTRONIC;
								}
								if (isToBeSubmitted) {
									// Preserve templateType when toggling signature with "to be submitted" active
									setDocumentsDataArray((prev) =>
										prev.map((doc) =>
											doc.documentWorkflowFileId === documentWorkflowFileId
												? { ...doc, signatureType: newSignatureType }
												: doc
										)
									);
								} else {
									onDataChange(
										documentWorkflowFileId,
										DOCUMENT_SETTINGS_MODAL_FIELDS.SIGNATURE_TYPE,
										newSignatureType
									);
								}
							}}
							disabled={!isRadioButtonSelected}
						>
							{tMipManage(`${tTemplateOptionsPrefix}.signature_required`)}
						</TSCheckbox>
						<InternalGroupWrapper
							className={clsx({
								disabled: !isSignatureRequired || isSignatureSubOptionsLocked,
							})}
						>
							<Text fz={DEFAULT_TEXT_SIZE} fw={SEMI_BOLD_FONT_WEIGHT}>
								{tMipManage(`${tTemplateOptionsPrefix}.signature_type.title`)}
							</Text>
							<SignatureTypeRow>
								<SignatureTypeSwitchWrapper>
									<AdvancedSwitch
										twoWay={true}
										leftText={tMipManage(
											`${tTemplateOptionsPrefix}.signature_type.electronic`
										)}
										rightText={tMipManage(
											`${tTemplateOptionsPrefix}.signature_type.wet_ink`
										)}
										selectedValue={
											isRadioButtonSelected &&
											isSignatureRequired &&
											!isSignatureTypeElectronic &&
											!isSignatureSubOptionsLocked
										}
										onChange={() => onSignatureTypeSwitchClicked(signatureType)}
									/>
								</SignatureTypeSwitchWrapper>
								<TSCheckbox
									checked={isSignatureAdvanced}
									textColor={defaultTheme.v2.color.neutral[600]}
									disabled={!isSignatureTypeElectronic}
									onChange={() =>
										onDataChange(
											documentWorkflowFileId,
											DOCUMENT_SETTINGS_MODAL_FIELDS.IS_SIGNATURE_ADVANCED,
											!isSignatureAdvanced
										)
									}
								>
									{tMipManage(
										'investors.simple_document_workflow.template_section.template_options.signature_type.advanced'
									)}
								</TSCheckbox>
							</SignatureTypeRow>
						</InternalGroupWrapper>
						<InternalGroupWrapper
							className={clsx({
								disabled: !isWitnessingAvailable,
							})}
						>
							<Text fz={DEFAULT_TEXT_SIZE} fw={SEMI_BOLD_FONT_WEIGHT}>
								{tMipManage(`${tTemplateOptionsPrefix}.witnessing.title`)}
							</Text>
							<SignatureTypeSwitchWrapper>
								<AdvancedSwitch
									twoWay={true}
									disabled={!isWitnessingAvailable}
									leftText={tMipManage(
										`${tTemplateOptionsPrefix}.witnessing.required`
									)}
									rightText={tMipManage(
										`${tTemplateOptionsPrefix}.witnessing.not_required`
									)}
									selectedValue={
										isWitnessingAvailable ? !witnessesRequired : true
									}
									onChange={() =>
										onDataChange(
											documentWorkflowFileId,
											DOCUMENT_SETTINGS_MODAL_FIELDS.WITNESSING_REQUIRED,
											!witnessesRequired
										)
									}
								/>
							</SignatureTypeSwitchWrapper>
						</InternalGroupWrapper>
						<TSCheckbox
							disabled={!isSignatureRequired || isSignatureSubOptionsLocked}
							checked={isCounterpartiesIncluded && !isSignatureSubOptionsLocked}
							onChange={() =>
								onDataChange(
									documentWorkflowFileId,
									DOCUMENT_SETTINGS_MODAL_FIELDS.COUNTERPARTIES_INCLUDED,
									!counterpartiesIncluded
								)
							}
						>
							{tMipManage(`${tTemplateOptionsPrefix}.counterparty.title`)}
						</TSCheckbox>
						<CounterpartyInputWrapper
							className={clsx({ disabled: !isCounterpartiesIncluded })}
						>
							<FormProvider {...counterpartyForm}>
								<form onSubmit={counterpartyForm.handleSubmit(onSubmit)}>
									<Controller
										name={COUNTERPARTY_FORM.NAME}
										control={counterpartyForm.control}
										render={({ field }) => (
											<StyledTextInput
												rightSection={
													<Icon size="lg" variant="arrow-right-v3" />
												}
												placeholder="Fund representative"
												description={tMipManage(
													`${tTemplateOptionsPrefix}.counterparty.fund_representative`
												)}
												inputWrapperOrder={['input', 'description']}
												value={field.value}
												onChange={(event) =>
													field.onChange(event.target.value)
												}
												disabled={!isCounterpartiesIncluded}
											/>
										)}
									/>

									<Controller
										name={COUNTERPARTY_FORM.AMOUNT}
										control={counterpartyForm.control}
										render={({ field }) => (
											<NumberInputWrapper>
												<StyledNumberInputGroup gap={5}>
													<ActionIcon
														size={SIGNATORY_STEPPER_ICON_SIZE}
														variant="transparent"
														onClick={() =>
															field.onChange(field.value - INPUT_STEP)
														}
														disabled={
															field.value === INPUT_MIN_AMOUNT ||
															!field.value
														}
													>
														<Icon
															size="lg"
															variant="remove"
															disabled={
																!isCounterpartiesIncluded ||
																field.value <= INPUT_MIN_AMOUNT
															}
														/>
													</ActionIcon>

													<StyledNumberInput
														value={field.value}
														placeholder="1"
														onChange={(value) => field.onChange(value)}
														max={INPUT_MAX_AMOUNT}
														min={INPUT_MIN_AMOUNT}
														step={INPUT_STEP}
														variant="unstyled"
														disabled={!isCounterpartiesIncluded}
														hideControls
													/>

													<ActionIcon
														size={SIGNATORY_STEPPER_ICON_SIZE}
														variant="transparent"
														onClick={() => {
															if (!field.value) {
																return field.onChange(1);
															}
															field.onChange(
																field.value + INPUT_STEP
															);
														}}
														disabled={field.value === INPUT_MAX_AMOUNT}
													>
														<Icon
															size="lg"
															variant="add"
															disabled={
																!isCounterpartiesIncluded ||
																field.value === INPUT_MAX_AMOUNT
															}
														/>
													</ActionIcon>
												</StyledNumberInputGroup>
												<Text fz={SMALL_TEXT_SIZE} ta="left">
													{tMipManage(
														`${tTemplateOptionsPrefix}.counterparty.signature_number`
													)}
												</Text>
											</NumberInputWrapper>
										)}
									/>
									<StyledIconButton
										size="sm"
										leftSection={<Icon variant="tick" />}
										variant="secondary"
										disabled={!isCounterpartiesIncluded}
										type="submit"
									/>
								</form>
							</FormProvider>
						</CounterpartyInputWrapper>

						<CounterpartiesChipsWrapper
							$showChips={!!counterparties.length}
							$disabled={!isCounterpartiesIncluded}
						>
							<ScrollableSection>
								{counterparties.map((counterparty) => (
									<ChipsV2
										key={counterparty.chipId}
										size="s"
										color={COLOR.LILAC}
										chipTheme={CHIP_THEME.LIGHT}
									>
										<ChipChildrenWrapper>
											{counterparty.name} ({counterparty.amount})
											<Icon
												variant="delete"
												onClick={() =>
													onOpenCounterpartyDeleteAlert(
														counterparty.chipId,
														counterparty.name
													)
												}
											/>
										</ChipChildrenWrapper>
									</ChipsV2>
								))}
							</ScrollableSection>
						</CounterpartiesChipsWrapper>
					</LeftPanel>
				</StyledGridColumn>
				<StyledGridColumn
					span={TWO_COLUMNS_PER_ROW}
					className={clsx({
						disabled: disabledConfig,
					})}
				>
					<RightPanel>
						<TSCheckbox
							checked={individualScheduleRequired}
							onChange={() =>
								onDataChange(
									documentWorkflowFileId,
									DOCUMENT_SETTINGS_MODAL_FIELDS.INDIVIDUAL_SCHEDULE_REQUIRED,
									!individualScheduleRequired
								)
							}
							disabled={!isRadioButtonSelected}
						>
							{tMipManage(`${tTemplateOptionsPrefix}.individual_schedule`)}
						</TSCheckbox>
						<TSCheckbox
							checked={isToBeSubmitted}
							onChange={() => {
								const newIsToBeSubmitted = !isToBeSubmitted;
								let newSignatureType = signatureType;
								if (newIsToBeSubmitted && isSignatureRequired) {
									newSignatureType = DocumentSignatureType.ELECTRONIC;
								} else if (newIsToBeSubmitted && !isSignatureRequired) {
									newSignatureType = DocumentSignatureType.SUBMITTABLE;
								} else if (!newIsToBeSubmitted && !isSignatureRequired) {
									newSignatureType = DocumentSignatureType.REVIEW;
								}
								setDocumentsDataArray((prev) =>
									prev.map((doc) =>
										doc.documentWorkflowFileId === documentWorkflowFileId
											? {
													...doc,
													isToBeSubmitted: newIsToBeSubmitted,
													signatureType: newSignatureType,
												}
											: doc
									)
								);
							}}
							disabled={!isRadioButtonSelected}
						>
							{tMipManage(`${tTemplateOptionsPrefix}.to_be_submitted`)}
						</TSCheckbox>

						<StyledDivider />
						<GuidanceNoteWrapper className={clsx({ disabled: !isRadioButtonSelected })}>
							<Text fz={DEFAULT_TEXT_SIZE} fw={NORMAL_FONT_WEIGHT}>
								{tMipManage(`${tTemplateOptionsPrefix}.guidance_note`)}
							</Text>
							<input {...getInputProps()} />
							{guidanceNote ? (
								<GuidanceNoteDocument
									documentTitle={guidanceNote.name}
									date={
										guidanceNote.createdAt
											? formatStringDateToUI(
													guidanceNote.createdAt,
													DATE_FORMATS.UI_SHORT
												)
											: undefined
									}
									documentID={guidanceNote.id}
									onClickDropdown={() => {
										setIsGuidanceDropdownOpen(!isGuidanceDropdownOpen);
									}}
									isDropdownOpen={isGuidanceDropdownOpen}
								>
									<DocumentCard.DropdownMenu>
										<DropdownDeleteField
											onClick={() =>
												removeGuidanceNote(documentWorkflowFileId)
											}
										>
											<RedText variant="sans/11/400">
												{tMipManage(
													'investors.simple_document_workflow.document_section.dropdown_menu.delete'
												)}
											</RedText>
										</DropdownDeleteField>
									</DocumentCard.DropdownMenu>
								</GuidanceNoteDocument>
							) : (
								<ButtonV2
									size="s"
									buttonTheme="light"
									variant="secondary"
									icon="sign"
									leftIcon
									onClick={open}
									disabled={isUploadingGuidanceNote ?? false}
								>
									{isUploadingGuidanceNote
										? tMipManage(
												'investors.simple_document_workflow.document_section.buttons.uploading'
											)
										: tMipManage(`${tTemplateOptionsPrefix}.attach_note`)}
								</ButtonV2>
							)}
							{showInvalidTypeView && (
								<ErrorText variant="sans/14/600">
									{tMipManage(`${tPrefix}.invalid_type_error`)}
								</ErrorText>
							)}
						</GuidanceNoteWrapper>
					</RightPanel>
				</StyledGridColumn>
				{canPlaceSignaturesManually && currentDocumentFileForPlacement ? (
					<Grid.Col span={ONE_COLUMN_PER_ROW}>
						<PlacementBlock>
							<PlacementHeaderRow>
								<Text fz={DEFAULT_TEXT_SIZE} fw={SEMI_BOLD_FONT_WEIGHT}>
									Place signatures manually
								</Text>
								<AdvancedSwitch
									selectedValue={!!useManualSignaturePlacement}
									onChange={(value) => toggleManualPlacement(!!value)}
								/>
							</PlacementHeaderRow>
							{useManualSignaturePlacement ? (
								<PlacementSummaryRow>
									{hasOverlappingPendingPlacements(signaturePlacements) ? (
										<Text fz={DEFAULT_TEXT_SIZE} c="red">
											{tMipManage(
												'investors.simple_document_workflow.template_section.placement_overlap'
											)}
										</Text>
									) : (
										<Text fz={DEFAULT_TEXT_SIZE} c="dimmed">
											{placedCount === totalSlots && totalSlots > 0
												? `All ${totalSlots} signature${totalSlots === 1 ? '' : 's'} placed`
												: `${placedCount} of ${totalSlots} placed`}
										</Text>
									)}
									<ButtonV2
										size="s"
										buttonTheme="light"
										variant="secondary"
										onClick={() => setIsPlacementModalOpen(true)}
									>
										{placedCount > 0
											? 'Edit placements'
											: 'Open placement editor'}
									</ButtonV2>
								</PlacementSummaryRow>
							) : null}
						</PlacementBlock>
						<Modal
							opened={isPlacementModalOpen && !!useManualSignaturePlacement}
							onClose={() => setIsPlacementModalOpen(false)}
							fullScreen
							withCloseButton={false}
							title="Place signatures"
							styles={{
								// Flex layout (not calc(100vh - 60px)): the Mantine title-bar
								// height isn't a known constant, so a calc clips the footer.
								content: {
									display: 'flex',
									flexDirection: 'column',
									height: '100vh',
								},
								body: {
									flex: 1,
									minHeight: 0,
									padding: '0 1rem 1rem 1rem',
									overflow: 'hidden',
								},
							}}
						>
							<PendingPlacementEditor
								slots={placementSlots}
								fileId={currentDocumentFileForPlacement.fileId}
								dealId={dealId}
								showStampMetadata={showStampMetadata ?? true}
								onPlacementsChange={updatePlacementsForCurrentDoc}
								onShowStampMetadataChange={updateShowStampMetadataForCurrentDoc}
								onClose={() => setIsPlacementModalOpen(false)}
							/>
						</Modal>
					</Grid.Col>
				) : null}
			</Grid>
		</Container>
	);
};

const PlacementBlock = styled.div`
	background-color: ${defaultTheme.v2.color.white[500]};
	padding: 0.75rem;
	border-radius: 8px;
	margin-top: 0.5rem;
`;

const PlacementHeaderRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	margin-bottom: 0.5rem;
`;

const PlacementSummaryRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
`;

const Container = styled.div`
	background-color: ${defaultTheme.v2.color.neutral[100]};
	padding: 0.5rem;
	border-radius: 8px;
	gap: 0.5rem;

	.disabled {
		cursor: default;
		opacity: 0.32;
		pointer-events: none;
	}

	${GuidanceNoteDocument} {
		margin-top: 0.5rem;
	}
`;

export { TemplateSection };
